import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Custom hook to prevent navigation when the user attempts to leave the page.
 * Displays a confirmation modal or the browser's native confirmation dialog.
 *
 * @param shouldBlock - Whether to block navigation attempts.
 * @param message - Message shown in the browser's native confirmation dialog (used on refresh/close).
 * @param shouldBlockNavigation - Optional function to determine if a specific navigation should be blocked.
 * @returns Object containing modal state and callbacks for handling confirmation.
 */
export function useNavigationBlocker(
  shouldBlock: boolean = true,
  message = "Changes you made may not be saved",
  shouldBlockNavigation?: (targetUrl: string) => boolean
) {
  const [warningModalOpen, setWarningModalOpen] = useState(false);
  const onLeaveRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!shouldBlock) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = message;
      return message;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [shouldBlock, message]);

  const triggerNavigationEvents = useCallback(() => {
    window.dispatchEvent(new Event("popstate"));
    window.dispatchEvent(new CustomEvent("locationchange"));
  }, []);

  useEffect(() => {
    if (!shouldBlock) return;

    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    const createBlockingFunction = (originalFn: typeof history.pushState) => {
      return function (...args: Parameters<typeof history.pushState>) {
        const [, , url] = args;
        const targetUrl = typeof url === "string" ? url : window.location.href;

        if (shouldBlockNavigation && !shouldBlockNavigation(targetUrl))
          return originalFn.apply(history, args);

        const proceed = () => {
          originalFn.apply(history, args);
          triggerNavigationEvents();
        };

        onLeaveRef.current = proceed;
        setWarningModalOpen(true);
        return;
      };
    };

    history.pushState = createBlockingFunction(originalPushState);
    history.replaceState = createBlockingFunction(originalReplaceState);

    const handlePopState = (event: PopStateEvent) => {
      event.preventDefault();

      const currentUrl = window.location.href;

      if (shouldBlockNavigation && !shouldBlockNavigation(currentUrl)) return;

      const proceed = () => {
        window.removeEventListener("popstate", handlePopState);
        setTimeout(() => history.go(-1), 0);
      };

      onLeaveRef.current = proceed;
      setWarningModalOpen(true);
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
      window.removeEventListener("popstate", handlePopState);
    };
  }, [shouldBlock, triggerNavigationEvents, shouldBlockNavigation]);

  const onLeave = useCallback(() => {
    onLeaveRef.current?.();
    onLeaveRef.current = null;
    setWarningModalOpen(false);
  }, []);

  const onCancel = useCallback(() => {
    onLeaveRef.current = null;
    setWarningModalOpen(false);
  }, []);

  return {
    warningModalOpen,
    onLeave,
    onCancel
  };
}
